import { SyntheticEvent, useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { When } from 'react-if'
import { Button } from '@/atoms/Button'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ParagraphLG, ParagraphSM, TitleLG, TitleMD } from '@/atoms/Text'
import { Durations } from '@/constants/durations'
import { Seo } from '@/layout/Seo'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { NextPageWithLayout } from '@/layouts/types'
import { useSafeAnalytics } from '@/utils/analytics'
import { getStringFromLocalStorage, removeFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'
import { ScannerComponent } from '@/views/ScannerView/Scanner'

function getTimeFromScanDate(scanDate: Date, currentTime: number) {
  const seconds = (currentTime - scanDate.getTime()) / 1000
  const minutes = seconds / 60
  return `${(minutes > 0 ? minutes : 1).toFixed(0)} minutes ago`
}

const variants = {
  open: { opacity: 1, y: '0' },
  closed: { opacity: 0, y: '-100%' },
}
type ValidateType = {
  isValid: boolean
  message: string | null
  dateRedeemed: Date | null
  isCodeSet: boolean
  code: string
  scanDateTime?: Date | null
}

const validTheaterCodes = [
  'angel-pass-2023',
  'angel-pass-philippines',
  '2023-angel-pass',
  'angel-pass-sof-2023',
  'angel-philippines-pass',
  'sof2023',
]

const Pass: NextPageWithLayout = () => {
  const { track } = useSafeAnalytics()
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [validations, setValidations] = useState<ValidateType[] | null>(null)
  const [shouldShowAlert, setShouldShowAlert] = useState<boolean>(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [hideLogout, setHideLogout] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [currentTime, setCurrentTime] = useState<number>(new Date().getTime())

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().getTime())
    }, 60000)

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [])

  const handleAuthSubmit = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault()
      if (validTheaterCodes.includes(inputValue)) {
        writeToLocalStorage('ANGEL_PASS_AUTH', inputValue)
        setIsAuthenticated(true)
        return setInputValue('')
      }
      setError('Not a valid theater code. Please try again.')
    },
    [inputValue],
  )

  const handleLogout = useCallback(() => {
    removeFromLocalStorage('ANGEL_PASS_AUTH')
    setIsAuthenticated(false)
  }, [])

  useEffect(() => {
    const code = getStringFromLocalStorage('ANGEL_PASS_AUTH')
    if (code && validTheaterCodes.includes(code)) {
      return setIsAuthenticated(true)
    }
  }, [])

  const onSuccess = useCallback(
    async (decodedText: string) => {
      const baseUrl = process.env.NEXT_PUBLIC_ANGEL_PASS as string
      const response = await axios.post<ValidateType>(`${baseUrl}/validate`, { code: decodedText }).catch((err) => {
        setHideLogout(true)
        setShouldShowAlert(true)
        setError(err.message)
      })

      setValidations((prev): ValidateType[] | null => {
        const dataWScanDate = response?.data
        if (dataWScanDate) dataWScanDate.scanDateTime = new Date()
        if (!prev && response?.data) return [response?.data]
        if (prev && (response?.data as ValidateType)) {
          return [dataWScanDate as ValidateType, ...prev]
        } else {
          return prev
        }
      })

      if (response?.data.isValid) {
        setSuccessMessage(response?.data.message)
        track('Angel Pass Scan', {
          ...response?.data,
          scanDate: new Date(),
        })
        setHideLogout(true)
        return setShouldShowAlert(true)
      }
      if (response?.data.isValid === false && response?.data?.message) {
        setError(response?.data?.message)
        track('Angel Pass Scan', {
          ...response?.data,
          scanDate: new Date(),
        })
        setHideLogout(true)
        return setShouldShowAlert(true)
      }
      setError('Something went wrong! Please try again.')
      setHideLogout(true)
      setShouldShowAlert(true)
    },
    [track],
  )

  const onError = useCallback(() => {
    //   do nothing
  }, [])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (successMessage) {
      timer = setTimeout(() => {
        setHideLogout(false)
        setShouldShowAlert(false)
        setSuccessMessage(null)
      }, 2500)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [successMessage])

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (error) {
      timer = setTimeout(() => {
        setHideLogout(false)
        setShouldShowAlert(false)
        setError(null)
      }, 2500)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [error])

  useEffect(() => {
    setTimeout(() => {
      setHideLogout(false)
      setShouldShowAlert(true)
    }, 2000)
  }, [])

  const url = useCanonicalUrl(`/pass`)

  return (
    <>
      <Seo
        canonical={url}
        description="Angel Pass Scanner"
        noindex
        nofollow
        openGraph={{ title: 'Angel Pass', description: 'Angel Pass Scanner', url }}
        title="Angel Pass"
      />
      <PaddedContainer className="min-h-screen bg-white p-1 text-center">
        <When condition={isAuthenticated}>
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: '-100%' }}
              animate={shouldShowAlert ? 'open' : 'closed'}
              variants={variants}
              className={classNames(
                'h-32 absolute top-0 right-0 w-full text-white flex items-center justify-center z-[9999]',
                successMessage && 'bg-oxide',
                error && 'bg-red',
              )}
            >
              {successMessage && <p className="z-10 text-4xl font-semibold">{successMessage}</p>}
              {error && <p className="z-10 text-xl font-semibold text-white">{error}</p>}
            </motion.div>
          </AnimatePresence>
          <Button
            onClick={handleLogout}
            variant={'black'}
            outline
            className={classNames(
              'text-md ml-auto mt-4 w-fit px-3 py-1.5 cursor-pointer z-[99999999]',
              hideLogout && 'opacity-0',
            )}
          >
            Logout
          </Button>
          <TitleLG className="mt-6 md:mt-16" weight="bold">
            Angel Pass
          </TitleLG>
          <ParagraphSM color="core-gray-600" className="mx-auto mt-2 max-w-sm text-center">
            Place the QR code inside the frame for scanning, or enter an Angel Pass code in the input.
          </ParagraphSM>
          <div className="mt-8">
            <ScannerComponent onSuccess={onSuccess} onError={onError} />
          </div>

          <div>
            <div className="mx-auto mt-20 flex max-w-3xl items-center justify-between">
              <TitleMD color="core-gray-950">Scan History</TitleMD>
              <Button
                onClick={() => {
                  setValidations(null)
                }}
                variant="black"
                outline
                disabled={!validations || validations?.length === 0}
              >
                Clear
              </Button>
            </div>
            <div className="mx-auto mt-4 flex max-w-3xl flex-col text-xs font-light">
              <When condition={validations && validations?.length > 0}>
                {validations?.map((validation, index) => (
                  <div
                    key={index}
                    className={classNames(
                      'flex flex-col items-start border border-transparent border-t-core-gray-300 py-2 relative',
                    )}
                  >
                    <p
                      className={classNames(
                        'absolute top-3 right-2 p-1 px-2 rounded-full font-medium text-white',
                        validation?.isValid ? 'bg-[#DEFFF0] text-green' : 'bg-[#FFC9BB] text-red',
                      )}
                    >
                      {validation?.isValid ? 'Redeemed' : 'Failed'}
                    </p>
                    <p>
                      <span className="font-bold">Valid:</span> {validation?.isValid ? 'Valid' : 'Invalid'}
                    </p>
                    <p>
                      <span className="font-bold">Code:</span> {validation?.code}
                    </p>
                    <p>
                      <span className="font-bold">Message:</span> {validation?.message}
                    </p>
                    <p>
                      <span className="font-bold">Date:</span>{' '}
                      {new Date(validation?.dateRedeemed ?? validation.scanDateTime ?? '').toLocaleString()}
                    </p>
                    <p className="absolute bottom-2 right-2 italic opacity-50">
                      {getTimeFromScanDate(new Date(validation.scanDateTime ?? ''), currentTime)}
                    </p>
                  </div>
                ))}
              </When>
              <When condition={!validations || validations.length === 0}>
                <ParagraphLG className="mt-6" color="core-gray-400">
                  Empty
                </ParagraphLG>
              </When>
            </div>
          </div>
        </When>
        <When condition={!isAuthenticated}>
          <div className="mt-32 flex flex-col gap-8">
            <div>
              <TitleLG className="mt-6" weight="bold">
                Angel Pass
              </TitleLG>
              <ParagraphSM color="core-gray-600" className="mt-2 text-center">
                Please login with your valid theater code.
              </ParagraphSM>
            </div>
            <form className="mx-auto flex w-full max-w-lg flex-col gap-4" onSubmit={handleAuthSubmit}>
              <div className="w-full">
                <label className="block text-left">Theater Code</label>
                <input
                  placeholder="1234"
                  onChange={(e) => setInputValue(e.target.value)}
                  value={inputValue}
                  className={classNames('border py-2 px-2 rounded-md w-full', error && 'border-red')}
                  type="text"
                />
                {error && <p className="text-red">{error}</p>}
              </div>
              <Button disabled={inputValue.length < 3} className="w-full" type="submit" variant="black">
                Log in
              </Button>
            </form>
          </div>
        </When>
      </PaddedContainer>
    </>
  )
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/pass/index.tsx' },
  async ({ locale }) => {
    return {
      props: {
        ...(await loadTranslations(locale, ['common'])),
      },
      revalidate: Durations.ONE_DAY_IN_SECONDS,
    }
  },
)

Pass.getLayout = (page) => {
  return page
}

export const getStaticPaths = getGenericStaticPaths

export default Pass
